/**
 * Studio — Rule Sets list. Optional product_id from query (#/studio/rule-sets?product_id=...).
 */

import { useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import { useRuleSets } from "../../../api/opsV2/hooks";

type StudioRuleSetsPageProps = {
  userName?: string;
  productIdFromQuery?: string | null;
  onBack?: () => void;
  onOpenRuleSet?: (id: string) => void;
};

function getProductIdFromHash(): string | null {
  const hash = window.location.hash;
  const idx = hash.indexOf("?");
  if (idx === -1) return null;
  const params = new URLSearchParams(hash.slice(idx + 1));
  return params.get("product_id");
}

export function StudioRuleSetsPage({
  userName = "Utilisateur",
  productIdFromQuery,
  onBack,
  onOpenRuleSet,
}: StudioRuleSetsPageProps) {
  const productId = productIdFromQuery ?? getProductIdFromHash();
  const { data: ruleSets, loading, error, refetch } = useRuleSets(productId || null);

  useEffect(() => {
    refetch();
  }, [productId, refetch]);

  const list = useMemo(() => ruleSets ?? [], [ruleSets]);

  return (
    <AppShell
      userName={userName}
      topbar={
        <div className="flex items-center gap-4">
          {onBack && (
            <button type="button" onClick={onBack} className="text-sm text-muted hover:text-text">
              Studio
            </button>
          )}
          <span className="text-sm font-medium text-text">
            Rule Sets {productId ? "(produit filtré)" : ""}
          </span>
        </div>
      }
    >
      <div className="ds-container py-6">
        <h1 className="text-xl font-semibold text-text mb-2">Rule Sets</h1>
        <p className="text-sm text-muted mb-4">
          {productId
            ? "Rule Sets pour ce produit. Cliquez pour ouvrir le détail et gérer les règles."
            : "Tous les Rule Sets de l’organisation. Utilisez le lien depuis le Catalogue (Orchestration) pour filtrer par produit."}
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement…
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
            {error.message}
            <button type="button" onClick={() => refetch()} className="ml-2 underline">
              Réessayer
            </button>
          </div>
        )}
        {!loading && !error && list.length === 0 && (
          <p className="text-sm text-muted py-6">Aucun Rule Set.</p>
        )}
        {!loading && !error && list.length > 0 && (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-text">Nom / Version</th>
                  <th className="text-left p-3 font-medium text-text">Statut</th>
                  <th className="text-left p-3 font-medium text-text">Produit</th>
                  <th className="text-left p-3 font-medium text-text">Publié le</th>
                  <th className="text-left p-3 font-medium text-text w-24"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((rs) => (
                  <tr
                    key={rs.id}
                    className="border-b border-border/50 hover:bg-muted/20"
                  >
                    <td className="p-3">
                      <span className="font-medium text-text">
                        {rs.name ?? `v${rs.version_no}`}
                      </span>
                      <span className="ml-2 text-muted">v{rs.version_no}</span>
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          rs.status === "published"
                            ? "rounded bg-success/20 px-1.5 py-0.5 text-xs text-success"
                            : "rounded bg-muted px-1.5 py-0.5 text-xs text-muted"
                        }
                      >
                        {rs.status}
                      </span>
                    </td>
                    <td className="p-3 text-muted">{rs.product_id.slice(0, 8)}…</td>
                    <td className="p-3 text-muted">
                      {rs.published_at
                        ? new Date(rs.published_at).toLocaleDateString("fr-CA", {
                            dateStyle: "medium",
                          })
                        : "—"}
                    </td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => onOpenRuleSet?.(rs.id)}
                        className="text-sm text-primary hover:underline"
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
